/**
 * HPC Jobs type definitions.
 *
 * Mirrors the data model from the HPC Batch system. Jobs are returned by the
 * proxy Lambda which calls the awsmap CLI in the HPC account.
 */

/** Possible states of an AWS Batch job. */
export enum JobStatus {
  SUBMITTED = "SUBMITTED",
  PENDING = "PENDING",
  RUNNABLE = "RUNNABLE",
  STARTING = "STARTING",
  RUNNING = "RUNNING",
  SUCCEEDED = "SUCCEEDED",
  FAILED = "FAILED",
}

/** A single HPC Batch job as displayed in the jobs table. */
export interface HpcJob {
  /** Unique AWS Batch job ID. */
  jobId: string
  /** Human-readable job name. */
  jobName: string
  /** Username of the person who submitted the job. */
  user: string
  /** Optional workflow ID grouping related jobs. */
  workflowId?: string
  /** Current job status. */
  status: JobStatus
  /** Reason for the current status (e.g. failure reason). */
  statusReason?: string
  /** When the job was submitted. */
  submittedAt: Date
  /** When the job started running. */
  startedAt?: Date
  /** When the job stopped (completed or failed). */
  stoppedAt?: Date
  /** Size of the array job (if applicable). */
  arraySize?: number
  /** Status breakdown for array jobs. */
  arrayStatuses?: Record<JobStatus, number>
  /** Job IDs that this job depends on (must complete before this job runs). */
  dependsOn?: string[]
  /** Estimated total cost in USD (only present if pricing data is available). */
  totalUsd?: number
  /**
   * Explicit workflow name returned by the backend (from the workflow_name field).
   * Present only if the backend provides it for this job.
   */
  workflowName?: string
}

/** Filter state for the workflows table. */
export interface HpcJobFilters {
  /** Text search applied to the workflow name. */
  jobName: string
  /** Text search applied to user. */
  user: string
}

/**
 * Possible states of a workflow.
 *
 * This is the workflow's own status, worked out by the HPC backend from its
 * tasks (map-hpc `docs/WORKFLOW_LIFECYCLE_TABLE.md`). It differs from
 * JobStatus: a workflow stays RUNNING until every task has finished, even if
 * one has already failed, and has states a job never has.
 */
export enum WorkflowStatus {
  /** Submitted; no task has started yet. */
  SUBMITTED = "SUBMITTED",
  /** At least one task has not finished yet. */
  RUNNING = "RUNNING",
  /** All tasks succeeded, but the outputs are still being exported to S3. */
  EXPORTING = "EXPORTING",
  /** All tasks succeeded (and any outputs were exported). */
  SUCCEEDED = "SUCCEEDED",
  /** Finished with at least one failed task. */
  FAILED = "FAILED",
  /** At least one task was cancelled by a user. */
  CANCELLED = "CANCELLED",
  /** Older workflow whose outcome could not be worked out. */
  UNKNOWN = "UNKNOWN",
}

/**
 * One workflow in the workflows table, as returned by the /workflows endpoint
 * (`awsmap batch workflows --json`).
 */
export interface HpcWorkflow {
  /** Unique workflow ID, `wf-<UUID>`. */
  workflowId: string
  /** Name from `workflow.py`. Undefined for some older workflows. */
  workflowName?: string
  /** Username of the person who submitted the workflow. */
  user: string
  /** Kind of workflow, e.g. "maphpc", "mosquito" or "image_build". */
  workflowKind: string
  /** The workflow's own status. */
  status: WorkflowStatus
  /** When the workflow was submitted. */
  submittedAt: Date
  /** When the first task started running, if it has. */
  startedAt?: Date
  /** When the workflow reached a final status, if it has. */
  completedAt?: Date
  /** Number of tasks. An array job counts as one task. */
  taskCount: number
  /**
   * Number of tasks in each job status, e.g. { RUNNING: 1, SUCCEEDED: 2 }.
   * Statuses with no tasks may be missing.
   */
  taskStateCounts: Partial<Record<JobStatus, number>>
  /** Tasks that have not finished yet. */
  activeTaskCount: number
  /** Tasks that have finished. */
  completedTaskCount: number
  /**
   * When the status and counts were last recalculated. The backend refreshes
   * them about once a minute while the workflow is active.
   */
  countsRefreshedAt: Date
  /**
   * Earliest time of the next refresh. Undefined once the workflow is
   * finalised, meaning its status and counts will not change again.
   */
  nextRefreshAt?: Date
  /**
   * Estimated compute cost so far in USD, from the hourly rates estimated at
   * submit and each task's runtime. Tasks without a rate are left out.
   * Undefined when no task has a rate (image builds, older workflows).
   */
  estimatedCostUsd?: number
}
